import React, { useEffect, useState } from 'react';
import { Card, CardFooter, CardBody, Row, Col, Form } from 'reactstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useHistory, useParams } from 'react-router-dom';
import { find } from 'lodash';
import { parse, endOfDay, startOfDay } from 'date-fns';
import { Button, Field, FormAlerts, useFetch } from '@cordata/buffet';
import PropTypes from 'prop-types';
import { all, spread } from 'axios';

import usePatient from '~/utils/patient/hook';
import { authorizedInstance as axios, authorizedPyInstance as Py } from '~/utils';

import consts from '~/utils/cit/const';
import { deburrObject } from '~/utils/api/normalizers';

import DrugOverdosesForm from './DrugOverdose';
import SurvivorInteraction from './SurvivorInteraction';
import OtherInteraction from './OtherInteraction';
import LocationForm from './Location';
import TreatmentReferral from './TreatmentReferral';
import CIT from './CIT';

import UglySerializer from './ugly-serializer';

const TIMEZONES = [
  { label: 'Eastern Time', value: 'America/New_York' },
  { label: 'Central Time', value: 'America/Chicago' },
  { label: 'Mountain Time', value: 'America/Denver' },
  { label: 'Mountain Time - Arizona', value: 'America/Phoenix' },
  { label: 'Pacific Time', value: 'America/Los_Angeles' },
  { label: 'Alaska Time', value: 'America/Anchorage' },
  { label: 'Hawaii Time', value: 'Pacific/Honolulu' },
];

const showField = (f) => {
  if (!f) return false;

  return f.show;
};

const requireField = (f, s) => {
  if (!f) return s ? s.notRequired() : false;

  if (s) {
    return f.required && f.show
      ? s.required()
      : s.notRequired();
  }

  return f.required;
};

const validationSchema = Yup.object().shape({
  medicalEvent: Yup.mixed().required(),
  eventDate: Yup.date()
    .when(['medicalEvent', 'status', 'currentIbrEventDate'], (me, s, ibr, sch) => {
      let schema = sch;

      if (
        me?.attributes.medicalEventName === 'other_interaction' ||
        me?.attributes.medicalEventName === 'survivor_interaction' ||
        me?.attributes.medicalEventName === 'treatment_referral'
      ) {
        schema = schema.min(startOfDay(ibr));
      }

      if (
        (
          me?.attributes.medicalEventName === 'other_interaction' ||
          me?.attributes.medicalEventName === 'survivor_interaction'
        ) &&
        me?.attributes.propertyRules?.status.required &&
        s?.attributes.statusName !== 'scheduled'
      ) {
        schema = schema.max(endOfDay(new Date()));
      }

      if (
        me?.attributes.medicalEventName === 'treatment_referral'
      ) {
        schema = schema.max(endOfDay(new Date()));
      }

      if (
        me?.attributes.medicalEventName === 'drug_overdose'
      ) {
        schema = schema.max(endOfDay(new Date()));
      }

      return schema.required();
    }),

  eventTime: Yup.string().when('medicalEvent', (v, s) =>
    requireField(v.attributes.propertyRules?.eventTime, s)),

  eventTimeZone: Yup.mixed().when('medicalEvent', (v, s) =>
    requireField(v.attributes.propertyRules?.eventTimeZone, s)),

  status: Yup.mixed().when('medicalEvent', (v, s) =>
    requireField(v.attributes.propertyRules?.status, s)),

  provider: Yup.mixed().when('medicalEvent', (v, s) =>
    requireField(v.attributes.propertyRules?.provider, s)),

  reason: Yup.mixed().when('medicalEvent', (v, s) =>
    requireField(v.attributes.propertyRules?.reason, s)),

  facility: Yup.mixed().when('medicalEvent', (v, s) =>
    requireField(v.attributes.propertyRules?.facility, s)),

  isHomeAddress: Yup.string().when('medicalEvent', (v, s) =>
    requireField(v.attributes.propertyRules?.location, s)),

  naloxoneAdministered: Yup.string().when('medicalEvent', (v, s) =>
    (v.attributes.medicalEventName === 'drug_overdose'
      ? s.required()
      : s.notRequired())),
  naloxoneAdministeredBy: Yup.string().when(['medicalEvent', 'naloxoneAdministered'], {
    is: (mE, n) => (mE.attributes.medicalEventName === 'drug_overdose' && n === 'yes'),
    then: Yup.string().required(),
    otherwise: Yup.string().notRequired(),
  }),
  amountOfNaloxoneGiven: Yup.number().positive(),
  wasOverdoseFatal: Yup.string().when('medicalEvent', (v, s) =>
    (v.attributes.medicalEventName === 'drug_overdose'
      ? s.required()
      : s.notRequired())),
  acceptedTreatment: Yup.string().when(['medicalEvent', 'wasOverdoseFatal'], {
    is: (mE, n) => (mE.attributes.medicalEventName === 'drug_overdose' && n !== 'yes'),
    then: Yup.string().required(),
    otherwise: Yup.string().notRequired(),
  }),
  contactAttemptType: Yup.string().when('medicalEvent', (v, s) =>
    (v.attributes.medicalEventName === 'survivor_interaction'
      ? s.required()
      : s.notRequired())),
  contactPhase: Yup.string().when('medicalEvent', (v, s) =>
    (v.attributes.medicalEventName === 'survivor_interaction'
      ? s.required()
      : s.notRequired())),
  survivorContacted: Yup.string().when('medicalEvent', (v, s) =>
    (v.attributes.medicalEventName === 'survivor_interaction'
      ? s.required()
      : s.notRequired())),
  kitsDistributed: Yup.number().positive().integer(),
  ftStripsDistributed: Yup.number().positive().integer(),
  xyStripsDistributed: Yup.number().positive().integer(),
  leftFlyer: Yup.string().when('medicalEvent', (v, s) =>
    (v.attributes.medicalEventName === 'survivor_interaction'
      ? s.required()
      : s.notRequired())),
  type: Yup.string().when('medicalEvent', (v, s) =>
    (v.attributes.medicalEventName === 'other_interaction'
      ? s.required()
      : s.notRequired())),
  method: Yup.string().when('medicalEvent', (v, s) =>
    (v.attributes.medicalEventName === 'other_interaction'
      ? s.required()
      : s.notRequired())),
});

const initalValues = {
  // eventDate: props.data
  //   ? parse(props.data.attributes.eventDate.split('+')[0])
  //   : new Date(),
  // eventTime: props.data
  //   ? format(parse(props.data.attributes.eventDate.split('+')[0]), 'HH:mm')
  //   : format(new Date(), 'HH:mm'),
  // eventTimeZone: props.data
  //   ? find(TIMEZONES, tz => tz.label === props.data.attributes.eventTimeZone)
  //   : find(TIMEZONES, tz => tz.label === 'Eastern Time'),
};

const MedicalEventsForm = ({ data, providers: pr }) => {
  const params = useParams();
  const history = useHistory();
  const [, patient] = usePatient();

  const [, aMedicalEvent] = useFetch('data/reference/reference-medical-event', {
    params: { medicalEventName: params.refMedicalEventName },
    instance: 'p',
  });

  const [medicalEvent, setMedicalEvent] = useState();
  const [statuses, setStatuses] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [providers, setProviders] = useState(pr);
  const [citLoading, setCitLoading] = useState(true);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (aMedicalEvent) {
      setMedicalEvent(aMedicalEvent[0]);
    }
  }, [aMedicalEvent]);

  useEffect(() => {
    if (medicalEvent) {
      medicalEvent.attributes.propertyRules = JSON.parse(medicalEvent.attributes.propertyRules);

      if (!pr && showField(medicalEvent.attributes.propertyRules.provider)) {
        axios
          .get('providers/mv')
          .then(r => r.data.data)
          .then(setProviders);
      }

      if (showField(medicalEvent.attributes.propertyRules.facility)) {
        axios
          .get('reference/facilities')
          .then(r => r.data.data)
          .then(setFacilities);
      }

      if (showField(medicalEvent.attributes.propertyRules.status)) {
        axios
          .get(`reference/reference-statuses?type=medical_event__${medicalEvent.attributes.medicalEventName}`)
          .then(r => r.data.data)
          .then(setStatuses);
      }

      if (showField(medicalEvent.attributes.propertyRules.reason)) {
        axios
          .get(`reference/reference-reasons?type=medical_event__${medicalEvent.attributes.medicalEventName}`)
          .then(r => r.data.data)
          .then(setReasons);
      }

      if (medicalEvent.attributes.medicalEventName === 'cit') {
        all([
          Py.get('api/data/reference/reference-gender', { params: { 'all-data': 1, sort: 'default-sort-order,gender-label' } }),
          Py.get('api/data/reference/reference-race', { params: { 'all-data': 1, sort: 'default-sort-order,race-label' } }),
          Py.get('api/data/reference/reference-country-division', { params: { 'all-data': 1, sort: 'default-sort-order,country-division-label' } }),
          Py.get('api/data/reference/reference-referral-source', { params: { 'all-data': 1, sort: 'default-sort-order,referral-source-label' } }),
          Py.get('api/data/reference/reference-status', { params: { 'all-data': 1, sort: 'default-sort-order,status-label', ref_data_type: 'Episode' } }),
          Py.get('api/data/public/facility', { params: { 'is-in-selections': true } }),
        ])
          .then(spread((g, r, cd, rs, s, f) => {
            setFormData({
              ...consts.formData,
              states: cd.data.data.map(deburrObject),
              genders: g.data.data.map(deburrObject),
              races: r.data.data.map(deburrObject),
              referralSourceOptions: rs.data.data.map(deburrObject),
              dispositions: s.data.data.map(deburrObject),
              facilities: f.data.data.map(deburrObject),
            });

            setCitLoading(false);
          }));
      }
    }
  }, [medicalEvent]);

  return (
    <Formik
      displayName="MedicalEventsForm"
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize

      validationSchema={validationSchema}

      initialValues={{
        currentIbrEventDate: parse(patient.currentInboundReferral.eventDate),
        ...initalValues,
        ...(data ? data.attributes : data),
        ...params,
        id: params.medical_event_id,
        detailObject: data ? JSON.parse(data.attributes.detailObject) : {},
        medicalEvent,
        status: data && data.relationships.referenceStatus,
        provider: data && data.relationships.provider,
        facility: data && data.relationships.facility,
        reason: data && data.relationships.referenceReason,
        location: data && data.relationships.location,
        eventDate: data
          ? parse(data.attributes.eventDate.split('+')[0])
          : new Date(),
        eventTime: data
          ? data.attributes.eventDate.split('T')[1].split('.')[0]
          : medicalEvent?.attributes?.propertyRules.eventTime &&
            medicalEvent?.attributes?.propertyRules.eventTime.required &&
            new Date(),
        eventTimeZone: data
          ? find(TIMEZONES, tz => tz.label === data.attributes.eventTimeZone)
          : find(TIMEZONES, tz => tz.label === 'Eastern Time'),
      }}

      onSubmit={(values, { setErrors, setSubmitting }) => {
        const p = UglySerializer(values);

        if (!values.id) {
          axios
            .post('medical-events', p)
            .then(() => history.push(`/patients/${params.patient_id}/face-sheet`))
            .catch(e => setErrors({ server: e.errors }))
            .then(() => setSubmitting(false));
        } else {
          axios
            .put(`medical-events/${params.medical_event_id}`, p)
            .then(() => history.push(`/patients/${params.patient_id}/face-sheet`))
            .catch(e => setErrors({ server: e.errors }))
            .then(() => setSubmitting(false));
        }
      }}
    >
      {({
        handleSubmit,
        isSubmitting,
        submitCount,
        errors,
      }) => (
        <Card>
          <Form onSubmit={handleSubmit} noValidate>
            <CardBody>
              { submitCount >= 1 && errors && <FormAlerts errors={errors} /> }

              <Row form>
                {showField(medicalEvent?.attributes?.propertyRules.status) && (
                  <Col sm>
                    <Field
                      name="status"
                      type="select"
                      options={statuses}
                      getOptionValue={o => o.id}
                      getOptionLabel={o => o.attributes.statusLabel}
                      required={medicalEvent?.attributes?.propertyRules.status.required}
                    />
                  </Col>
                )}

                {showField(medicalEvent?.attributes?.propertyRules.reason) && (
                  <Col sm>
                    <Field
                      name="reason"
                      type="select"
                      options={reasons}
                      getOptionValue={o => o.id}
                      getOptionLabel={o => o.attributes.reasonLabel}
                      required={medicalEvent?.attributes?.propertyRules.reason.required}
                    />
                  </Col>
                )}
              </Row>

              <Row form>
                <Col sm>
                  <Field
                    name="eventDate"
                    type="date"
                    required
                  />
                </Col>

                {showField(medicalEvent?.attributes?.propertyRules.eventTime) && (
                  <Col sm>
                    <Field
                      name="eventTime"
                      type="time"
                      required={medicalEvent?.attributes?.propertyRules.eventTime.required}
                    />
                  </Col>
                )}

                {showField(medicalEvent?.attributes?.propertyRules.eventTimeZone) && (
                  <Col sm>
                    <Field
                      name="eventTimeZone"
                      type="select"
                      options={TIMEZONES}
                      required={medicalEvent?.attributes?.propertyRules.eventTimeZone.required}
                    />
                  </Col>
                )}
              </Row>

              {showField(medicalEvent?.attributes?.propertyRules.location) && (
                <LocationForm
                  rules={medicalEvent.attributes.propertyRules.location}
                />
              )}

              <Row form>
                {showField(medicalEvent?.attributes?.propertyRules.facility) && (
                  <Col sm>
                    <Field
                      name="facility"
                      type="select"
                      options={facilities}
                      getOptionValue={o => o.id}
                      getOptionLabel={o => o.name || o.attributes.facilityName}
                      required={medicalEvent?.attributes?.propertyRules.facility.required}
                    />
                  </Col>
                )}

                {showField(medicalEvent?.attributes?.propertyRules.provider) && (
                  <Col sm>
                    <Field
                      name="provider"
                      type="select"
                      options={providers}
                      getOptionValue={o => o.id}
                      getOptionLabel={o => o.attributes.displayName}
                      required={medicalEvent?.attributes?.propertyRules.provider.required}
                    />
                  </Col>
                )}
              </Row>

            </CardBody>

            {medicalEvent?.attributes?.medicalEventName === 'drug_overdose' && (
              <DrugOverdosesForm />
            )}

            {medicalEvent?.attributes?.medicalEventName === 'survivor_interaction' && (
              <SurvivorInteraction />
            )}

            {medicalEvent?.attributes?.medicalEventName === 'other_interaction' && (
              <OtherInteraction />
            )}

            {medicalEvent?.attributes?.medicalEventName === 'treatment_referral' && (
              <TreatmentReferral />
            )}

            {medicalEvent?.attributes?.medicalEventName === 'cit' && !citLoading && (
              <CIT formData={formData} />
            )}

            <hr />

            <CardBody>
              <Field
                name="note"
                type="textarea"
              />
            </CardBody>

            <CardFooter className="d-flex">
              <div className="ml-auto">
                <Button
                  type="cancel"
                  disabled={isSubmitting}
                  onClick={history.goBack}
                >
                  Cancel
                </Button>

                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Working ...' : 'Save'}
                </Button>
              </div>
            </CardFooter>
          </Form>
        </Card>
      )}
    </Formik>
  );
};

MedicalEventsForm.propTypes = {
  data: PropTypes.shape().isRequired,
  providers: PropTypes.arrayOf(PropTypes.shape()),
};

MedicalEventsForm.defaultProps = {
  providers: undefined,
};

export default MedicalEventsForm;
